'use client'

import { useInvestorCasesForStaff, useMyInvestorCases } from '@/features/investors'
import { useCaseDetail, useCasePreview } from '@/features/cases/hooks/use-case-detail'
import type { FlowType, ServiceType } from '@/features/cases/types'

export const SERVICE_TYPE_TO_FLOW: Record<ServiceType, FlowType> = {
  portfolio_audit: 'audit',
  trades_audit: 'trades',
  build_portfolio: 'build',
}

/** Resolves the investor's own case for a given flow — picks the most-recently-created one if
 * there's somehow more than one of the same service type. Returns `summary: null` (not an error)
 * when no case of that flow exists yet — a legitimate, common state, not a failure.
 *
 * Only fetches full case detail once the match is `published` — `GET /cases/:id`'s investor
 * branch 404s for anything else (`assertCaseAccess`, by design: the case isn't unlocked yet), so
 * fetching it earlier would surface that 404 as a real error instead of the legitimate "pending"
 * state. A caller only needs `summary.caseStatus` to render that state (see `PendingRequestCard`),
 * never the full detail. */
export function useMyCaseForFlow(flow: FlowType) {
  const casesQuery = useMyInvestorCases()
  const match = casesQuery.data
    ?.filter((c) => SERVICE_TYPE_TO_FLOW[c.serviceType] === flow)
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))[0]
  const caseDetailQuery = useCaseDetail(match?.caseStatus === 'published' ? match.id : undefined)
  return { summary: match ?? null, isListLoading: casesQuery.isLoading, ...caseDetailQuery }
}

/** Staff-side counterpart to `useMyCaseForFlow`, for the "Preview client dashboard" feature — same
 * shape, but resolves "which case" from an explicit `investorId` (via the staff-accessible
 * `GET /investors/:id/cases`) instead of the signed-in session's own identity, and reads the case
 * itself through `GET /cases/:id/preview` (investor-shaped, publish-gate skipped) rather than the
 * staff-shaped `GET /cases/:id` — so the preview can never accidentally leak a staff-only field
 * (note, seriousnessStatus, investor block) into what's meant to simulate the investor's own view. */
export function usePreviewCaseForFlow(investorId: string, flow: FlowType) {
  const casesQuery = useInvestorCasesForStaff(investorId)
  const match = casesQuery.data
    ?.filter((c) => SERVICE_TYPE_TO_FLOW[c.serviceType] === flow)
    .sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1))[0]
  const caseDetailQuery = useCasePreview(match?.id)
  return { summary: match ?? null, isListLoading: casesQuery.isLoading, ...caseDetailQuery }
}
